// Union of marketplaces from the two backend sources carried on a business profile:
//   - Amazon Ads profiles   (amazonAdsAccounts[].amazonAdsProfiles[]) — queryable, carry profileId
//   - Selling Partner mkts   (amazonSPAccounts[].marketplaces[])      — seller-enrolled
//
// Each entry is tagged with `sources: { ads, seller }`. A marketplace present in both an ads
// profile and a seller connection is matched by the canonical AXXXXXX id
// (`marketplaceStringId` ↔ `marketplaceId`) — NOT by country (an ads GB profile reports
// countryCode "UK" while SP reports "GB"; see helpers/spLinkStatus.js for the same join).
//
// Seller-only marketplaces (an SP marketplace with no matching ads profile) carry
// `profileId: null` and are NOT queryable yet: the OLAP backend resolves seller_id from the
// ads-profile X-Profile-Id header, so without an ads profile there is nothing to scope by.
// Consumers therefore show an info-state instead of data when one is selected.
//
// The embedded (manager) SP account shape has NO accountType, so seller-only entries default
// to the SELLER category (storefront icon, allowed on seller-only features).

import { countryCodeLabel } from '@/@core/utils/formatters.js';
import { spMarketplaces } from '@/@data/spMarketplaces.js';
import { ACCOUNT_TYPE } from '@/helpers/marketplaceAccountType.js';

export const MP_GROUP_CODE = Object.freeze({
  ADS: 'amazonAdsProfiles',
  SELLER: 'amazonSpAccounts',
});

// Stable identity for selection / highlight / dedup. Resolves from whatever fields an object
// carries, so it survives localStorage round-trips and works for raw ads profiles, normalized
// union entries, and seller-only entries alike.
export const getMarketplaceUid = (mp) => {
  if (!mp) return null;
  if (mp.marketplaceUid) return mp.marketplaceUid;
  if (mp.profileId) return `ads:${mp.profileId}`;
  if (mp.sellingPartnerId && mp.marketplaceStringId) {
    return `sp:${mp.sellingPartnerId}:${mp.marketplaceStringId}`;
  }
  return null;
};

// Canonical ids covered by any SP connection of the profile.
export const spCoverageSet = (profile) =>
  new Set(
    (profile?.amazonSPAccounts || [])
      .flatMap((a) => a.marketplaces || [])
      .map((m) => m.marketplaceId)
      .filter(Boolean),
  );

// Canonical ids that already exist as ads profiles.
export const adsCoverageSet = (profile) =>
  new Set(
    (profile?.amazonAdsAccounts || [])
      .flatMap((a) => a.amazonAdsProfiles || [])
      .map((p) => p.marketplaceStringId)
      .filter(Boolean),
  );

// Selectable as a marketplace? Mirrors the dropdown: Attribution / Agency ads profiles are not.
export const isSelectableAdsProfile = (p) => {
  const type = (p?.accountType || '').toLowerCase();
  return !!p?.profileId && type !== 'attribution' && type !== 'agency';
};

// Normalize an ads profile into a union entry — spread for back-compat so every existing
// consumer of `currentMarketplace.*` keeps working; just add `marketplaceUid` + `sources`.
export const normalizeAdsMarketplace = (profile, spIds) => ({
  ...profile,
  marketplaceUid: `ads:${profile.profileId}`,
  sources: { ads: true, seller: spIds.has(profile.marketplaceStringId) },
});

// Normalize a single seller-only SP marketplace into a union entry (no ads profile).
// store_name (the storefront name, e.g. "Athletic Wonder") is the seller label; mp.name is the
// canonical marketplace name (e.g. "Amazon.com.mx") and stays as a catalog fallback only.
export const normalizeSellerOnlyMarketplace = (account, mp) => {
  const storeLabel = mp.storeName || mp.name;
  return {
    marketplaceUid: `sp:${account.sellingPartnerId}:${mp.marketplaceId}`,
    marketplaceStringId: mp.marketplaceId,
    profileId: null,
    sellingPartnerId: account.sellingPartnerId,
    countryCode: mp.countryCode,
    name: mp.name,
    sellerName: storeLabel,
    accountName: account.alias || storeLabel,
    accountType: ACCOUNT_TYPE.SELLER,
    currencyCode: mp.defaultCurrencyCode,
    storeName: mp.storeName,
    marketplaceType: mp.marketplaceType,
    timeZone: null,
    sources: { ads: false, seller: true },
  };
};

// Seller "marketplaces" that aren't real Amazon retail storefronts — Amazon "Stores"
// non-marketplaces (e.g. "Non-Amazon CA") and shadow marketplaces. The backend tags these
// with marketplace_type; we hide the known junk types. Add to the set if the backend adds more.
const HIDDEN_MARKETPLACE_TYPES = new Set(['SHADOW']);
export const isHiddenMarketplaceType = (mp) =>
  HIDDEN_MARKETPLACE_TYPES.has(mp?.marketplaceType);

// All seller-only marketplaces of the profile (SP marketplaces with no matching ads profile),
// excluding hidden (shadow / non-retail) types. Deduped by full uid (sellingPartnerId +
// marketplaceId), NOT by marketplaceId — so the SAME marketplace covered by several seller
// accounts yields one entry per seller id (the third tree level groups those under the marketplace).
export const buildSellerOnlyMarketplaces = (profile) => {
  const adsIds = adsCoverageSet(profile);
  const seen = new Set();
  const out = [];
  for (const account of profile?.amazonSPAccounts || []) {
    for (const mp of account.marketplaces || []) {
      const id = mp.marketplaceId;
      if (!id || adsIds.has(id)) continue;
      if (isHiddenMarketplaceType(mp)) continue; // skip shadow / non-retail
      const entry = normalizeSellerOnlyMarketplace(account, mp);
      if (seen.has(entry.marketplaceUid)) continue;
      seen.add(entry.marketplaceUid);
      out.push(entry);
    }
  }
  return out;
};

const REGION_LABELS = { NA: 'North America', EU: 'Europe', FE: 'Asia / Far East', OTHER: 'Other' };
const REGION_ORDER = ['NA', 'EU', 'FE', 'OTHER'];

const catalogEntry = (marketplaceStringId) =>
  spMarketplaces.find((m) => m.marketplaceId === marketplaceStringId) || null;

export const sellerLeafLabel = (leaf) =>
  leaf.sellerName || leaf.accountName || leaf.name || leaf.sellingPartnerId || '—';

// Build the hierarchical marketplace tree for el-tree-select:
//   region (NA/EU/FE/Other) → marketplace (canonical AXXXXXX) → seller id (only when a
//   marketplace has more than one seller/profile). A marketplace with a single entry is
//   itself the selectable leaf (no third level). Region nodes and multi-seller marketplace
//   nodes are `disabled` (grouping only — not selectable).
//
// Returns { tree, leafByUid } where leafByUid maps a leaf's marketplaceUid → the marketplace
// entry, so the component can resolve a selection back to the entry it applies.
export const buildMarketplaceTree = (profile) => {
  const leafByUid = new Map();
  if (!profile) return { tree: [], leafByUid };

  const spIds = spCoverageSet(profile);
  const seenProfileIds = new Set();
  const adsLeaves = (profile.amazonAdsAccounts || [])
    .flatMap((a) => a.amazonAdsProfiles || [])
    .filter((p) => {
      if (!isSelectableAdsProfile(p) || seenProfileIds.has(p.profileId)) return false;
      seenProfileIds.add(p.profileId);
      return true;
    })
    .map((p) => normalizeAdsMarketplace(p, spIds));

  // Seller-only marketplaces (shadow / non-retail already excluded at the source).
  const sellerLeaves = buildSellerOnlyMarketplaces(profile);

  const leaves = [...adsLeaves, ...sellerLeaves];

  // region -> Map(marketplaceStringId -> leaf[])
  const byRegion = new Map();
  for (const leaf of leaves) {
    leafByUid.set(leaf.marketplaceUid, leaf);
    const region = catalogEntry(leaf.marketplaceStringId)?.region || 'OTHER';
    if (!byRegion.has(region)) byRegion.set(region, new Map());
    const mpMap = byRegion.get(region);
    const key = leaf.marketplaceStringId || leaf.marketplaceUid;
    if (!mpMap.has(key)) mpMap.set(key, []);
    mpMap.get(key).push(leaf);
  }

  const tree = [];
  for (const region of REGION_ORDER) {
    const mpMap = byRegion.get(region);
    if (!mpMap || mpMap.size === 0) continue;

    const marketplaces = [...mpMap.entries()]
      .map(([mpId, list]) => {
        const cat = catalogEntry(mpId);
        const countryCode = cat?.countryCode || list[0].countryCode || '';
        const mpName = cat?.name || list[0].name || sellerLeafLabel(list[0]);
        return { mpId, list, countryCode, mpName };
      })
      .sort((a, b) => a.mpName.localeCompare(b.mpName));

    const children = marketplaces.map(({ mpId, list, countryCode, mpName }) => {
      // Single seller for this marketplace → this row IS the leaf, labeled with the seller
      // name. The country code is `displayCode` (GB shown as UK), drawn before the flag, so
      // the label itself has no " (XX)" suffix. Multiple sellers → the marketplace name is
      // the branch header and the seller names sit underneath.
      const displayCode = countryCodeLabel(countryCode);
      if (list.length === 1) {
        const sellerName = sellerLeafLabel(list[0]);
        return {
          value: list[0].marketplaceUid,
          label: sellerName,
          kind: 'marketplace-leaf',
          countryCode,
          displayCode,
          marketplace: list[0],
        };
      }
      return {
        value: `mp:${region}:${mpId}`,
        label: mpName,
        kind: 'marketplace-branch',
        countryCode,
        displayCode,
        disabled: true,
        children: list
          .slice()
          .sort((a, b) => sellerLeafLabel(a).localeCompare(sellerLeafLabel(b)))
          .map((leaf) => {
            const leafCode = leaf.countryCode || countryCode;
            return {
              value: leaf.marketplaceUid,
              label: sellerLeafLabel(leaf),
              kind: 'seller-leaf',
              countryCode: leafCode,
              displayCode: countryCodeLabel(leafCode),
              marketplace: leaf,
            };
          }),
      };
    });

    tree.push({
      value: `region:${region}`,
      label: REGION_LABELS[region],
      kind: 'region',
      disabled: true,
      children,
    });
  }

  return { tree, leafByUid };
};

// Flatten a marketplace tree to its selectable leaf entries (the `marketplace` payload on
// marketplace-leaf / seller-leaf nodes). Used to resolve a selected node value back to its
// entry and to answer "does this profile have any seller-only marketplace?".
export const flattenMarketplaceLeaves = (tree) => {
  const out = [];
  const walk = (nodes) => {
    for (const node of nodes || []) {
      if (node.marketplace) out.push(node.marketplace);
      if (node.children) walk(node.children);
    }
  };
  walk(tree);
  return out;
};

// Resolve a marketplaceUid back to the live union entry on a profile — the single
// uid → marketplace resolver used by URL/deep-link restore. Matches against the SAME
// selectable leaf set the dropdown shows (buildMarketplaceTree), so a restored selection is
// always one the user could have picked. Returns null when the uid isn't (or is no longer)
// present on the profile — callers then fall back to their default-marketplace heuristic.
export const findMarketplaceByUid = (profile, uid) => {
  if (!profile || !uid) return null;
  return buildMarketplaceTree(profile).leafByUid.get(uid) ?? null;
};

// Default marketplace for a profile when no uid resolves (no URL / server / stored intent, or a
// stale one): the first SELECTABLE leaf of the dropdown's own tree — Ads leaves first (sellerName,
// then synced, then first, the long-standing ads heuristic), then seller-only `sp:` leaves.
// Zero leaves -> null. Never returns an entry the dropdown would not offer.
export const findDefaultMarketplace = (profile) => {
  const leaves = [...buildMarketplaceTree(profile).leafByUid.values()];
  const adsLeaves = leaves.filter((l) => l.profileId);
  return (
    adsLeaves.find((l) => l.sellerName?.trim()) ||
    adsLeaves.find((l) => l.syncedConfiguration) ||
    adsLeaves[0] ||
    leaves[0] ||
    null
  );
};

// IANA time zone of a profile's default marketplace (findDefaultMarketplace, the same pick the
// dropdown falls back to), for pages whose own marketplace has no zone: seller-only
// marketplaces carry `timeZone: null`. When that default has none either (a seller-only leaf
// picked because the profile has no Ads leaf), the first Ads profile with a zone answers.
// null when the profile has no zone at all. Cached per profile object (a found zone only), since
// date cells ask once per row.
const profileZoneCache = new WeakMap();
export const getProfileDefaultTimeZone = (profile) => {
  if (!profile || typeof profile !== 'object') return null;
  const cached = profileZoneCache.get(profile);
  if (cached) return cached;
  const zone =
    findDefaultMarketplace(profile)?.timeZone ||
    (profile.amazonAdsAccounts || [])
      .flatMap((account) => account.amazonAdsProfiles || [])
      .find((p) => p?.timeZone)?.timeZone ||
    null;
  if (zone) profileZoneCache.set(profile, zone);
  return zone;
};
